'use strict';
const stateService = require('./state.js');
const hrefService = require('./href.js');
const linksService = require('./router-links.js');
const validationService = require('./validate.js');
const configService = require('./config.js');
const httpService = require('./http.js');

class Router {
  constructor(routes, config) {
    // Classes
    this.Route = require('./route.js');

    // Callbacks
    this.navigateWillStartCallbacks = [];
    this.onNavigateStartCallbacks = [];
    this.onNavigateEndCallbacks = [];
    this.routeWillBootstrapCallbacks = [];
    this.routeDidBootstrapCallbacks = [];

    // Temporary
    this.httpRequests = [];
    this.currentlyNavigating = false;
    this.initialBootstrap = true;

    this.routes = null;
    this.config = configService.getConfig({});
    this.setupInitialBootstrap();
  }

  setRoutes(routes) {
    this.routes = validationService.validateRoutes(routes);
    this.route = hrefService.getRouteFromHref(this.routes, document.location.pathname);
    this.setLocation(hrefService.parseHrefToRouterLocation(this.route, document.location.pathname));
    this.lastRoute = null;
  }

  setConfig(config) {
    this.config = configService.getConfig(config);
  }

  setupInitialBootstrap() {
    document.addEventListener("DOMContentLoaded", () => {
      if (this.routes != null &&
          this.initialBootstrap &&
          this.config.autoInitialBootstrap) this.bootstrap();
    });
  }

  bootstrap() {
    this.callRouteWillBootstrapCallbacks();

    if (this.initialBootstrap) {
      linksService.setupRouterLinks(this);
      stateService.setupPopStateService(this);
      stateService.replaceHistoryState(document.location.pathname);
      this.initialBootstrap = false;
    }

    if (typeof this.route.service.bootstrap === 'function') this.route.service.bootstrap(this.route);
    // if (this.config.scrollToTopOnBootstrap) window.scrollTo( 0, 0 );
    this.callRouteDidBootstrapCallbacks();
  }


  /*
    Route service to change page
    Route needs to be looked up to call Router service
  */
  navigate(href) {
    // If config.navigateIfCurrent is false, we block navigating if href is current
    if ((!this.config.navigateIfCurrent) &&
        (hrefService.getRouteFromHref(this.routes, href) == this.route)) return;
    this.navigateMain(href, true);
  }
  navigateByRoute(route, href) {
    // If config.navigateIfCurrent is false, we block navigating if href is current
    if ((!this.config.navigateIfCurrent) &&
        (route == this.route)) return;
    this.navigateMain(href, true, {route});
  }

  navigatePopState(href) {
    this.navigateMain(href, false, null);
  }

  navigateMain(href, doPushState, options) {
    // If route service currently loading, cancel request
    if (this.currentlyNavigating) this.cancelRouteService(this.route.service);

    if (this.route.service.routeWillUnmount) this.route.service.routeWillUnmount();

    this.callArrayOfCallbacks(this.navigateWillStartCallbacks);
    this.lastRoute = this.route;

    // If a route was provided, we don't need to parse the href for it
    if (options != null) {
      if (options.route) this.route = options.route;
    }
    else this.route = hrefService.getRouteFromHref(this.routes, href);

    this.setLocation(hrefService.parseHrefToRouterLocation(this.route, href));
    this.callArrayOfCallbacks(this.onNavigateStartCallbacks);
    if (doPushState) stateService.pushHistoryState(this.location.pathname);
    // If the route has no defined service
    if (this.route.service) this.callRouteService(this.route.service, href);
    else this.resolveNavigate();
  }

  /*
    Route service has been resolved
    New page data/components have been loaded
  */
  resolveNavigate() {
    this.bootstrap();
    this.currentlyNavigating = false;
    this.callArrayOfCallbacks(this.onNavigateEndCallbacks);
    if (this.lastRoute.service.routeDidUnmount) this.lastRoute.service.routeDidUnmount();
  }

  /*
    Call route service
  */
  callRouteService(routeService, href) {
    this.currentlyNavigating = true;
    routeService.load(this.resolveNavigate.bind(this));
  }

  /*
    Calls service cancel methods
    Aborts current HTTP requests
  */
  cancelRouteService(routeService) {
    if (typeof routeService.cancel === 'function') routeService.cancel(this.currentlyNavigating);
    for (let i = 0; i < this.httpRequests.length; i += 1) {
      this.httpRequests[i].req.abort();
    }
    this.httpRequests = [];
    this.currentlyNavigating = false;
  }


  /*
    Event Hooks
  */
  navigateWillStart(callback) {
    this.navigateWillStartCallbacks.push(callback);
  }
  onNavigateStart(callback) {
    this.onNavigateStartCallbacks.push(callback);
  }
  onNavigateEnd(callback) {
    this.onNavigateEndCallbacks.push(callback);
  }

  /*
    routeWillBootstrap
  */
  routeWillBootstrap(callback) {
    this.routeWillBootstrapCallbacks.push(callback);
  }
  callRouteWillBootstrapCallbacks() {
    this.forEachCallback(this.routeWillBootstrapCallbacks, (callback) => {
      callback(this.route);
    });
  }

  /*
    routeDidBootstrap
  */
  routeDidBootstrap(callback) {
    this.routeDidBootstrapCallbacks.push(callback);
  }
  callRouteDidBootstrapCallbacks() {
    this.forEachCallback(this.routeDidBootstrapCallbacks, (callback) => {
      callback(this.route);
    });
  }


  callArrayOfCallbacks(callbacksArray) {
    for (let i = 0; i < callbacksArray.length; i += 1) {
      callbacksArray[i]();
    }
  }

  forEachCallback(callbacksArray, callback) {
    for (let i = 0; i < callbacksArray.length; i += 1) {
      callback(callbacksArray[i]);
    }
  }

  post(url, data, callback) {
    let reqId = url + Date.now();
    let req = httpService.post(url, data, function() {
      if (req.readyState === 4) {
        if (req.status === 200) callback(false, req);
        else callback(true, req);
      }
      this.removeHttpRequestFromStack(reqId);
    }.bind(this));

    this.httpRequests.push({
      id: reqId,
      req: req
    });
    return req;
  }

  get(url, callback) {
    let reqId = url + Date.now();
    let req = httpService.get(url, function() {
      if (req.readyState === 4) {
        if (req.status === 200) callback(false, req);
        else callback(true, req);
      }
      this.removeHttpRequestFromStack(reqId);
    }.bind(this));

    this.httpRequests.push({
      id: reqId,
      req: req
    });
    return req;
  }

  removeHttpRequestFromStack(id) {
    for (let i = 0; i < this.httpRequests.length; i += 1) {
      if (this.httpRequests[i].id === id) this.httpRequests.splice(i, 1);
    }
  }

  setLocation(locationObj) {
    this.location = locationObj;
  }
}

module.exports = Router;



// WEBPACK FOOTER //
// ../autor/src/router.js